import { formatVnd } from '@/lib/format'
import { m } from '@/lib/paraglide/messages'

/** How long MISA gets to answer before the system stops resending on its own
 *  and the person has to act; the server uses the same 30 minutes. */
export const UNANSWERED_WINDOW_MS = 30 * 60 * 1000

const VN_CLOCK = new Intl.DateTimeFormat('en-GB', {
    timeZone: 'Asia/Ho_Chi_Minh',
    hour: '2-digit',
    minute: '2-digit',
    hourCycle: 'h23',
})
const VN_DAY_MONTH = new Intl.DateTimeFormat('en-GB', {
    timeZone: 'Asia/Ho_Chi_Minh',
    day: '2-digit',
    month: '2-digit',
})

/** The code in "MISA [Code]: explanation", the way the server stores a MISA
 *  refusal; undefined when the message carries none. */
export function misaErrorCode(message?: string) {
    return /^MISA \[([^\]]+)\]/.exec(message ?? '')?.[1]
}

/** The one table from a MISA code to what the person should read. A code not
 *  listed is shown as MISA wrote it, with a pointer to the admin. */
const REFUSAL_SENTENCES: Record<string, () => string> = {
    InvalidInvoiceDate: () => m.einvoice_stuck_refused_date(),
    InvalidInvDate: () => m.einvoice_stuck_refused_date(),
    InvalidTaxCode: () => m.einvoice_stuck_refused_tax_code(),
    LicenseInfo_NotBuy: () => m.einvoice_stuck_refused_license(),
    LicenseInfo_OutOfInvoice: () => m.einvoice_stuck_refused_license(),
    LicenseInfo_Expired: () => m.einvoice_stuck_refused_license(),
}

export function refusalSentence(message?: string) {
    const code = misaErrorCode(message)
    const known = code ? REFUSAL_SENTENCES[code] : undefined
    if (known) return known()
    return message
        ? m.einvoice_stuck_report_after({ text: message })
        : m.einvoice_stuck_refused_unknown()
}

/** When the system stops resending an unanswered publication, as HH:MM in
 *  Vietnam, whatever the browser's timezone. */
export function waitingSentence(requestedAt: string) {
    const until = new Date(Date.parse(requestedAt) + UNANSWERED_WINDOW_MS)
    return m.einvoice_stuck_waiting({ time: VN_CLOCK.format(until) })
}

/** dd/mm of an instant in Vietnam. */
export const vnDayMonth = (instant: string) =>
    VN_DAY_MONTH.format(new Date(instant))

export function misaDraftSentence(createdAt?: string) {
    return createdAt
        ? m.einvoice_stuck_misa_draft_on({ date: vnDayMonth(createdAt) })
        : m.einvoice_stuck_misa_draft()
}

export function driftSentence(drift?: {
    order_total: number
    lines_total: number
    difference: number
}) {
    if (!drift) return m.einvoice_stuck_drift_plain()
    return m.einvoice_stuck_drift({
        order: formatVnd(drift.order_total),
        lines: formatVnd(drift.lines_total),
        gap: formatVnd(Math.abs(drift.difference)),
    })
}
